// OGSPluckerCamera — camera pose+intrinsics → [1,6,1,H,W] plücker tensor for the appearance decoder.
// GPU (GpuPlucker) or CPU (computePluckerRays); GpuPlucker is lazy-imported so the CPU/test path skips its .wgsl.

import type * as THREE from "three";
import { ORIGIN_ZERO, computePluckerRays } from "../../inference/plucker.ts";
import { ogsGpuFlags } from "../../inference/ogsGpuFlags.ts";
import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";
import type { GpuPlucker } from "../../inference/GpuPlucker";

export interface OGSPluckerCameraOptions {
  id: string;
  // Camera-origin scale = 1/worldScale; the loader resolves it from the descriptor's world_scale.
  originScale?: number;
  // Scene placement of the splat, in world units (scene.json world_offset).
  // Subtracted from the camera position BEFORE originScale, putting the origin
  // in the splat's local frame — see inference/plucker.ts for why the moment
  // channels need it and dir does not.
  worldOffset?: readonly number[];
  // scene.json world_rotation, as a quaternion — INVERTED onto the camera so appr
  // stays conditioned on the training frame while the mesh renders upright
  // (render/worldRotation.ts). null = the bundle declares none.
  worldRotation?: THREE.Quaternion | null;
  outputPort?: string;
}

// `?gpuplucker=0` opts the GPU path out (resolved in ogsGpuFlags with every
// other OGS kill-switch).
function gpuPluckerEnabled(): boolean {
  return ogsGpuFlags().gpuPlucker;
}

export class OGSPluckerCamera implements Node {
  readonly id: string;
  readonly type = "ogsPluckerCamera";
  readonly inputs: readonly PortSpec[] = [];
  readonly outputs: readonly PortSpec[];
  readonly bindings = [];

  private readonly outPort: string;
  private readonly originScale: number;
  private readonly worldOffset: readonly number[];
  private readonly worldRotation: THREE.Quaternion | null;
  private uvRes = 0;
  private device: GPUDevice | null = null;
  private useGpu = false;
  private gpu: GpuPlucker | null = null;

  constructor(opts: OGSPluckerCameraOptions) {
    this.id = opts.id;
    this.originScale = opts.originScale ?? 1;
    this.worldOffset = opts.worldOffset ?? ORIGIN_ZERO;
    this.worldRotation = opts.worldRotation ?? null;
    this.outPort = opts.outputPort ?? "plucker";
    this.outputs = [{ name: this.outPort }];
  }

  async init(ctx: NodeContext): Promise<void> {
    // The plücker feeds appr, so it is generated at the DECODER's resolution — the
    // lift's uvRes may be higher when the bundle declares decoder_res.
    this.uvRes = ctx.decoderRes ?? ctx.uvRes;
    // BOTH, and they are different questions. The GPU plücker hands appr an
    // `ort.Tensor.fromGpuBuffer`, so it needs the decoder IO to be on the device
    // as well as the device itself. Reading one field for both is what made
    // `?gpubuf=0` silently turn this off too — it now says what it needs.
    this.device = ctx.device;
    this.useGpu = !!ctx.device && ctx.gpuBufferIo && gpuPluckerEnabled();
    if (this.useGpu && this.device) {
      // Lazy: GpuPlucker pulls a .wgsl?raw module — keep it off the CPU/test path.
      const { GpuPlucker } = await import("../../inference/GpuPlucker");
      this.gpu = new GpuPlucker(this.device);
    }
  }

  async evaluate(frame: FrameInputs): Promise<Record<string, NodeTensor>> {
    const H = this.uvRes;
    if (this.gpu) {
      const tensor = this.gpu.compute(frame.camera, H, H, this.originScale, this.worldOffset, this.worldRotation);
      return { [this.outPort]: { loc: "gpu-buffer", tensor, dims: [1, 6, 1, H, H] } };
    }
    const data = computePluckerRays(frame.camera, H, H, this.originScale, this.worldOffset, this.worldRotation);
    return { [this.outPort]: { loc: "cpu", data, dims: [1, 6, 1, H, H] } };
  }

  /** 'GPU' | 'CPU' — the resolved mode, for the one-line pipeline log. */
  mode(): "GPU" | "CPU" {
    return this.gpu ? "GPU" : "CPU";
  }

  /** Read the last GPU plücker buffer back to CPU. DEBUG ONLY (the
   *  __ISAAC_GPU_PARITY__ console oracle) — never on the render path. Null on
   *  the CPU path (nothing to read back). */
  async debugReadback(): Promise<Float32Array | null> {
    return this.gpu ? this.gpu.debugReadback() : null;
  }

  dirtyOn(): readonly DirtySource[] {
    return ["camera"];
  }

  dispose(): void {
    this.gpu?.destroy();
    this.gpu = null;
  }
}
